// ISDP CUSTOM: the entity details screen's "About" card — ours, not Backstage's.
//
// Backstage's <EntityAboutCard> prints the description FIRST, offers no way to add a
// field, and renders "View Source" / "View TechDocs" action tiles. The owner asked for
// a Contact field separate from Owner and for the description to sit at the BOTTOM,
// under every other field — none of which is reachable from outside that component
// (the workaround it forced, AboutCardSourceGated, hid its source tile with a
// `display:contents` CSS hack). So the card is ours: info fields first, description
// last, and no action tiles at all — the details screen's own "View source" button
// (EntitySourceBar) is the one place that affordance lives.
//
// Values come from the shared entityHelpers and the shared category taxonomy, so
// Owner / System / Type / Lifecycle / Category read exactly as they do on the cards,
// in the docs category filter and on the Landscape. Every row is omitted when its
// value is unset — no row ever prints "unknown".
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { categoryFromAnnotations } from '@internal/plugin-isdp-common';
import { catalogMessages } from '../../i18n/catalog';
import { isValidEmail } from '../common/validation';
import {
  getLifecycle,
  getOwner,
  getSystem,
  getType,
  ownerLabel,
} from './entityHelpers';
import './EntityAboutCard.css';

/**
 * Author-set contact for the entry, separate from the owner group: the mailbox, team
 * or channel a reader should actually write to. Set self-serve in catalog-info.yaml:
 *   metadata.annotations:
 *     draeger.com/contact: platform-team@draeger.com     # mailbox → mailto: link
 *     draeger.com/contact: https://teams.microsoft.com/… # any URL → external link
 *     draeger.com/contact: Platform Team                 # anything else → plain text
 */
const ANNOTATION_CONTACT = 'draeger.com/contact';

/**
 * One label/value row of the field list.
 *
 * `block` puts the pair on its own full-width line with the label above the value, for a
 * field whose length is unbounded — the tag list. Packed into a narrow grid column it
 * wrapped to three lines while the columns beside it stayed empty.
 */
const Field = ({
  label,
  children,
  block,
}: {
  label: string;
  children: ReactNode;
  block?: boolean;
}) => (
  <>
    <dt
      className={`devhub-entity-about__label${
        block ? ' devhub-entity-about__label--block' : ''
      }`}
    >
      {label}
    </dt>
    <dd
      className={`devhub-entity-about__value${
        block ? ' devhub-entity-about__value--block' : ''
      }`}
    >
      {children}
    </dd>
  </>
);

/** The contact annotation rendered as a mailto/external link when it is one. */
const ContactValue = ({ value }: { value: string }) => {
  if (isValidEmail(value)) {
    return (
      <a className="devhub-entity-about__link" href={`mailto:${value}`}>
        {value}
      </a>
    );
  }
  if (/^https?:\/\//i.test(value)) {
    return (
      <a
        className="devhub-entity-about__link"
        href={value}
        target="_blank"
        rel="noreferrer"
      >
        {value}
      </a>
    );
  }
  return <>{value}</>;
};

/**
 * `wide` — set by EntityAboutRow when this card spans the whole row (no relations graph
 * beside it). It only widens the FIELD GRID to several label/value pairs per row; without
 * it a full-width card renders one thin column of fields against a lot of empty space.
 */
export const EntityAboutCard = ({ wide }: { wide?: boolean }) => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(catalogMessages);

  const owner = getOwner(entity);
  const contact = (
    entity.metadata.annotations?.[ANNOTATION_CONTACT] ?? ''
  ).trim();
  const system = getSystem(entity);
  const type = getType(entity);
  const lifecycle = getLifecycle(entity);
  const tags = entity.metadata.tags ?? [];
  const category = categoryFromAnnotations(entity.metadata.annotations);
  const description = entity.metadata.description;
  const namespace = entity.metadata.namespace || 'default';

  return (
    <div
      className={`devhub-entity-about${wide ? ' devhub-entity-about--wide' : ''}`}
    >
      <div className="devhub-entity-about__header">{t('about.title')}</div>
      <hr className="devhub-entity-about__divider" />
      <div className="devhub-entity-about__body">
        <dl className="devhub-entity-about__fields">
          {owner && (
            <Field label={t('about.owner')}>{ownerLabel(owner)}</Field>
          )}
          {contact && (
            <Field label={t('about.contact')}>
              <ContactValue value={contact} />
            </Field>
          )}
          {system && (
            <Field label={t('about.system')}>
              <Link
                className="devhub-entity-about__link"
                to={`/catalog/${namespace}/system/${system}`}
              >
                {system}
              </Link>
            </Field>
          )}
          {type && <Field label={t('about.type')}>{type}</Field>}
          {lifecycle && (
            <Field label={t('about.lifecycle')}>{lifecycle}</Field>
          )}
          {category && (
            <Field label={t('about.category')}>{category}</Field>
          )}
          {tags.length > 0 && (
            <Field label={t('about.tags')} block>
              <span className="devhub-entity-about__tags">
                {tags.map(tag => (
                  <odx-chip key={tag} variant="neutral">
                    {tag}
                  </odx-chip>
                ))}
              </span>
            </Field>
          )}
        </dl>
        {description && (
          <div className="devhub-entity-about__description">
            <span className="devhub-entity-about__label">
              {t('about.description')}
            </span>
            <p className="devhub-entity-about__description-text">
              {description}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
